// Avrah design tokens — single source of truth for color, surface, radius,
// motion. Mirrors DESIGN.md so the JS layer agrees with the design system.
// Import named constants for new code; reach for the default `tokens` object
// when wiring up themes or theme-shaped consumers.

// Studio-stage palette
export const studioBlack = "#0D0D0D";
export const studioBlackDeep = "#0a0a0a";
export const studioBlackLifted = "#1a1a1a";
export const marqueeGold = "#DAA520";
export const marqueeGoldLit = "#E8B432";
export const marqueeGoldDeep = "#B8860B";
export const marqueeGoldHighlight = "#FFD700";
export const stageLight = "#fff";

// Solid surface ladder — depth comes from stepping up a tier, never from a
// hairline or a translucent tint. A border is reserved for the selected
// element, where it is `selectedBorderWidth` thick.
export const surfaceRaised = studioBlackLifted;
export const surfaceRaisedHover = "#202020";
export const surfaceControl = "#2C2C2C";
export const surfaceControlHover = "#383838";
export const selectedBorderWidth = "2px";

// Solid text ladder — opaque greys to pair with the solid surfaces.
export const inkBody = "#D6D6D6";
export const inkSecondary = "#A8A8A8";
export const inkTertiary = "#8A8A8A";

// White-tint text ladder (Type Color Ladder, DESIGN.md §2)
export const textStrong = "rgba(255,255,255,0.9)";
export const textDefault = "rgba(255,255,255,0.85)";
export const textIcon = "rgba(255,255,255,0.78)";
export const textMuted = "rgba(255,255,255,0.55)";
export const textQuiet = "rgba(255,255,255,0.4)";
export const textFaint = "rgba(255,255,255,0.35)";

// Border / surface ladder — white-tint hairlines
export const borderHairline06 = "rgba(255,255,255,0.06)";
export const borderHairline08 = "rgba(255,255,255,0.08)";
export const borderHairline10 = "rgba(255,255,255,0.10)";
export const borderHairline12 = "rgba(255,255,255,0.12)";
export const borderHairline18 = "rgba(255,255,255,0.18)";
export const borderHairline22 = "rgba(255,255,255,0.22)";
export const borderHairline45 = "rgba(255,255,255,0.45)";

// Border / surface ladder — gold-tint hairlines
export const borderGold12 = "rgba(184,134,11,0.12)";
export const borderGold18 = "rgba(184,134,11,0.18)";
export const borderGold30 = "rgba(184,134,11,0.30)";
export const borderGold50 = "rgba(218,165,32,0.50)";

// Type: the site's one typeface (--font-sans in index.css); and the system's
// monospace, only for codes read character by character.
export const fontSans = '"Poppins", "Poppins Fallback", system-ui, -apple-system, sans-serif';
export const fontCode = "ui-monospace, SFMono-Regular, Menlo, monospace";

// Radii
export const pill = "980px";
export const lg = "20px";
export const md = "16px";
export const sm = "12px";
export const xs = "10px";

// Motion
export const easeOutQuart = "cubic-bezier(0.25, 1, 0.5, 1)";
export const fastMs = 200;
export const slowMs = 300;

const tokens = {
  color: {
    studioBlack,
    studioBlackDeep,
    studioBlackLifted,
    marqueeGold,
    marqueeGoldLit,
    marqueeGoldDeep,
    marqueeGoldHighlight,
    stageLight,
  },
  surface: {
    raised: surfaceRaised,
    raisedHover: surfaceRaisedHover,
    control: surfaceControl,
    controlHover: surfaceControlHover,
  },
  ink: {
    body: inkBody,
    secondary: inkSecondary,
    tertiary: inkTertiary,
  },
  text: {
    strong: textStrong,
    default: textDefault,
    icon: textIcon,
    muted: textMuted,
    quiet: textQuiet,
    faint: textFaint,
  },
  border: {
    hairline06: borderHairline06,
    hairline08: borderHairline08,
    hairline10: borderHairline10,
    hairline12: borderHairline12,
    hairline18: borderHairline18,
    hairline22: borderHairline22,
    hairline45: borderHairline45,
    gold12: borderGold12,
    gold18: borderGold18,
    gold30: borderGold30,
    gold50: borderGold50,
  },
  font: {
    sans: fontSans,
    code: fontCode,
  },
  radius: {
    pill,
    lg,
    md,
    sm,
    xs,
  },
  easing: {
    easeOutQuart,
  },
  duration: {
    fastMs,
    slowMs,
  },
};

export default tokens;
